import React, { useMemo, useRef } from 'react'
import {
	Box,
	Button,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Chip,
	ChipPropsVariantOverrides,
	CircularProgress,
	IconButton,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import AddIcon from '@mui/icons-material/Add'
import { WorkItem } from '../../../../../../types/Work/WorkItem'
import { StyledTableCell } from '../../../../../core/StyledTableCell'
import SearchBar from '../../../../../core/SearchBar'
import { LazyWorkItemTree } from '../../../../../core/lazyTree/useLazyWorkItemTree'
import { useVirtualTableRows } from '../../../../../core/virtualTable/useVirtualTableRows'
import {
	WorkItemsTabRow,
	getVisibleWorkItemsTabRows,
	hasWorkItemChildren,
} from './visibleWorkItemsTabRows'

type WorkItemsTabProps = {
	workItems: WorkItem[]
	tree: LazyWorkItemTree
	onAddTask: (taskId: number, path: string, activityType: 'reworkTask' | 'workItemTask') => void
	searchText: string
	handleSearchTextChange: (searchText: string) => void
}

const COLUMN_COUNT = 3

const WorkItemsTab = ({
	workItems,
	tree,
	onAddTask,
	searchText,
	handleSearchTextChange,
}: WorkItemsTabProps) => {
	const containerRef = useRef<HTMLDivElement>(null)
	const tableRef = useRef<HTMLTableElement>(null)
	const { expandedIds, childrenById, loadingIds, failedIds, toggle, retry } = tree
	const rows = useMemo(
		() => getVisibleWorkItemsTabRows(workItems, childrenById, expandedIds, loadingIds, failedIds),
		[workItems, childrenById, expandedIds, loadingIds, failedIds],
	)
	const { start, end, paddingTop, paddingBottom, measureRow } = useVirtualTableRows(
		rows,
		containerRef,
		tableRef,
		false,
	)

	const getLayerVariant = (workItem: WorkItem): keyof ChipPropsVariantOverrides => {
		return `Layer${workItem.wbsIndex}${
			workItem.outOfScope ? 'OutOfScope' : 'WorkItem'
		}` as keyof ChipPropsVariantOverrides
	}

	const renderRow = (row: WorkItemsTabRow, rowIndex: number) => {
		const ref = (element: HTMLTableRowElement | null) => measureRow(row.id, element)
		const { workItem, layer } = row

		if (row.kind === 'workItem') {
			const canExpand = (workItem.tasks?.length ?? 0) > 0 || hasWorkItemChildren(workItem)
			return (
				<TableRow key={row.id} ref={ref} aria-rowindex={rowIndex}>
					<StyledTableCell sx={{ paddingRight: '0' }}>
						{canExpand && (
							<IconButton
								aria-label='expand row'
								sx={{ pl: layer }}
								size='small'
								onClick={() => toggle(workItem)}
							>
								{expandedIds.has(workItem.id) ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
							</IconButton>
						)}
					</StyledTableCell>
					<StyledTableCell align='center'>
						<Chip label={workItem.typeName} variant={getLayerVariant(workItem)} />
					</StyledTableCell>
					<StyledTableCell align='left' sx={{ paddingLeft: layer * 2 }}>
						{workItem.name}
					</StyledTableCell>
				</TableRow>
			)
		}

		if (row.kind === 'task') {
			const { task, path } = row
			return (
				<TableRow key={row.id} ref={ref} aria-rowindex={rowIndex} style={{ background: '#fff' }}>
					<StyledTableCell sx={{ paddingRight: '0' }}>
						{!task.isQC && (
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
								<IconButton
									aria-label='add task'
									size='small'
									onClick={() => onAddTask(task.id, [...path, task.name].join(' > '), 'workItemTask')}
									color='primary'
								>
									<AddIcon />
								</IconButton>
							</Box>
						)}
					</StyledTableCell>
					<StyledTableCell align='center'>
						<Chip label='Task' variant='task' size='small' />
					</StyledTableCell>
					<StyledTableCell align='left' sx={{ paddingLeft: (layer + 1) * 2 }}>
						{task.name}
					</StyledTableCell>
				</TableRow>
			)
		}

		if (row.kind === 'rework') {
			const { rework, path } = row
			return (
				<TableRow key={row.id} ref={ref} aria-rowindex={rowIndex}>
					<StyledTableCell sx={{ paddingRight: '0' }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
							<IconButton
								aria-label='add task'
								size='small'
								onClick={() => onAddTask(rework.id, [...path, rework.name].join(' > '), 'reworkTask')}
								color='primary'
							>
								<AddIcon />
							</IconButton>
						</Box>
					</StyledTableCell>
					<StyledTableCell align='center'>
						<Chip label='Rework' variant='Rework' size='small' />
					</StyledTableCell>
					<StyledTableCell align='left' sx={{ paddingLeft: (layer + 1) * 2 }}>
						{rework.name}
					</StyledTableCell>
				</TableRow>
			)
		}

		return (
			<TableRow key={row.id} ref={ref} aria-rowindex={rowIndex}>
				<TableCell colSpan={COLUMN_COUNT} sx={{ p: 0, border: 0 }}>
					<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
						{row.kind === 'loading' ? (
							<CircularProgress size={24} />
						) : (
							<>
								Could not load this work item.
								<Button onClick={() => retry(workItem.id)}>Retry</Button>
							</>
						)}
					</Box>
				</TableCell>
			</TableRow>
		)
	}

	return (
		<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
			<SearchBar
				onChange={handleSearchTextChange}
				searchPlaceholder='Filter Work Items'
				showIncludeFilter={false}
				searchText={searchText}
			/>
			<TableContainer
				ref={containerRef}
				sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowAnchor: 'none' }}
			>
				{/* The table stays mounted while empty, so the virtual rows keep their scroll listener. */}
				<Table ref={tableRef} stickyHeader aria-rowcount={rows.length + 1}>
					{rows.length > 0 && (
						<TableHead>
							<TableRow>
								<StyledTableCell />
								<StyledTableCell align='center'>Type</StyledTableCell>
								<StyledTableCell align='left'>Name</StyledTableCell>
							</TableRow>
						</TableHead>
					)}
					<TableBody>
						{rows.length === 0 ? (
							<TableRow>
								<TableCell colSpan={COLUMN_COUNT} align='center' sx={{ py: 4, border: 0 }}>
									<Typography variant='body1'>No work items found.</Typography>
								</TableCell>
							</TableRow>
						) : (
							<>
								{paddingTop > 0 && (
									<TableRow aria-hidden='true'>
										<TableCell
											colSpan={COLUMN_COUNT}
											style={{ height: paddingTop, padding: 0, border: 0 }}
										/>
									</TableRow>
								)}
								{rows.slice(start, end).map((row, index) => renderRow(row, start + index + 2))}
								{paddingBottom > 0 && (
									<TableRow aria-hidden='true'>
										<TableCell
											colSpan={COLUMN_COUNT}
											style={{ height: paddingBottom, padding: 0, border: 0 }}
										/>
									</TableRow>
								)}
							</>
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	)
}

export default WorkItemsTab
